// 콘텐츠 블록 파싱 유틸리티

import { parseJSXProps } from "./problemAnswerUtils";

// 콘텐츠 블록 타입 정의
export type ContentBlock = {
  type: "table" | "block-math" | "text" | "code" | "mermaid" | "image" | "wrapping-block" | "red-alert";
  content: string;
  language?: string;
  props?: Record<string, string | number | boolean>;
  className?: string;
};

// 텍스트 내에서 콘텐츠 블록을 찾는 함수 (표, LaTeX, 텍스트, 이미지)
export const findContentBlocks = (text: string): ContentBlock[] => {
  const lines = text.split("\n");
  const blocks: ContentBlock[] = [];
  let currentTextLines: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // 코드 블록 감지 (``` - zero-width space 제거)
    const cleanedLine = trimmedLine.replace(/\u200B/g, ''); // Zero Width Space 제거
    if (cleanedLine.startsWith("```")) {
      if (currentTextLines.length > 0) {
        blocks.push({
          type: "text",
          content: currentTextLines.join("\n").trim(),
        });
        currentTextLines = [];
      }

      const language = cleanedLine.slice(3).trim();
      const codeLines = [];
      i++; // 다음 줄로 이동

      // 닫는 ``` 찾기 (더 안전한 방식)
      while (i < lines.length) {
        const currentLine = lines[i];
        if (
          currentLine.trim() === "```" ||
          currentLine.trim().startsWith("```")
        ) {
          break;
        }
        codeLines.push(currentLine);
        i++;
      }

      // prettier-ignore 주석만 제거
      const cleanedContent = codeLines
        .join("\n")
        .replace(/<!--\s*prettier-ignore\s*-->\n?/g, "");

      blocks.push({
        type: language === "mermaid" ? "mermaid" : "code",
        content: cleanedContent,
        language: language || "text",
      });

      i++; // 닫는 ``` 건너뛰기
      continue;
    }

    // wrappingBlock() 헬퍼로 감싼 콘텐츠 감지
    if (trimmedLine.includes("__WRAPPING_START__") && trimmedLine.includes("__WRAPPING_CONTENT__")) {
      if (currentTextLines.length > 0) {
        blocks.push({
          type: "text",
          content: currentTextLines.join("\n").trim(),
        });
        currentTextLines = [];
      }

      // wrappingBlock() 헬퍼로 감싼 콘텐츠 수집 (여러 줄에 걸쳐 있을 수 있음)
      const wrappingLines = [line];
      let wrappingText = line;

      // __WRAPPING_END__ 마커까지 찾기
      while (i < lines.length) {
        if (lines[i].includes("__WRAPPING_END__")) {
          wrappingLines.push(lines[i]);
          wrappingText += "\n" + lines[i];
          break;
        }
        if (i + 1 < lines.length) {
          i++;
          wrappingLines.push(lines[i]);
          wrappingText += "\n" + lines[i];
        } else {
          break;
        }
      }

      // className과 content 분리
      const startMatch = wrappingText.match(/__WRAPPING_START__([^_]+)__WRAPPING_CONTENT__([\s\S]+?)__WRAPPING_END__/);
      if (startMatch) {
        const className = startMatch[1];
        const wrappingContent = startMatch[2];

        blocks.push({
          type: "wrapping-block",
          content: wrappingContent,
          className: className,
        });
      }

      i++;
      continue;
    }

    // table() 헬퍼로 감싼 테이블 감지
    if (trimmedLine.includes("__TABLE_START__")) {
      if (currentTextLines.length > 0) {
        blocks.push({
          type: "text",
          content: currentTextLines.join("\n").trim(),
        });
        currentTextLines = [];
      }

      // table() 헬퍼로 감싼 테이블 수집 (여러 줄에 걸쳐 있을 수 있음)
      const tableLines = [line];
      let tableText = line;

      // __TABLE_END__ 마커까지 찾기
      while (i < lines.length) {
        if (lines[i].includes("__TABLE_END__")) {
          tableLines.push(lines[i]);
          tableText += "\n" + lines[i];
          break;
        }
        if (i + 1 < lines.length) {
          i++;
          tableLines.push(lines[i]);
          tableText += "\n" + lines[i];
        } else {
          break;
        }
      }

      // className과 content 분리
      const startMatch = tableText.match(/__TABLE_START__(.+?)__TABLE_CONTENT__([\s\S]+?)__TABLE_END__/);
      if (startMatch) {
        const className = startMatch[1];
        const tableContent = startMatch[2];

        blocks.push({
          type: "table",
          content: tableContent,
          className: className,
        });
      }

      i++;
      continue;
    }

    // ImageWithCaption 컴포넌트 감지
    if (trimmedLine.includes("<ImageWithCaption")) {
      if (currentTextLines.length > 0) {
        blocks.push({
          type: "text",
          content: currentTextLines.join("\n").trim(),
        });
        currentTextLines = [];
      }

      // ImageWithCaption 태그 수집 (여러 줄에 걸쳐 있을 수 있음)
      const componentLines = [line];
      let componentText = line;

      // 자체 닫힘 태그가 아니라면 닫는 태그까지 찾기
      if (!line.includes("/>")) {
        i++;
        while (i < lines.length) {
          componentLines.push(lines[i]);
          componentText += "\n" + lines[i];
          if (
            lines[i].includes("/>") ||
            lines[i].includes("</ImageWithCaption>")
          ) {
            break;
          }
          i++;
        }
      }

      // JSX 속성 파싱
      const props = parseJSXProps(componentText);

      blocks.push({
        type: "image",
        content: componentText,
        props: props,
      });

      i++;
      continue;
    }

    // RedAlert 컴포넌트 감지
    if (trimmedLine.includes("<RedAlert")) {
      if (currentTextLines.length > 0) {
        blocks.push({
          type: "text",
          content: currentTextLines.join("\n").trim(),
        });
        currentTextLines = [];
      }

      // RedAlert 태그 수집 (여러 줄에 걸쳐 있을 수 있음)
      const componentLines = [line];
      let componentText = line;

      // 자체 닫힘 태그가 아니라면 닫는 태그까지 찾기
      if (!line.includes("/>")) {
        i++;
        while (i < lines.length) {
          componentLines.push(lines[i]);
          componentText += "\n" + lines[i];
          if (
            lines[i].includes("/>") ||
            lines[i].includes("</RedAlert>")
          ) {
            break;
          }
          i++;
        }
      }

      // JSX 속성 파싱
      const props = parseJSXProps(componentText);

      blocks.push({
        type: "red-alert",
        content: componentText,
        props: props,
      });

      i++;
      continue;
    }

    // 블록 수식 감지 ($$...$$)
    if (
      trimmedLine.startsWith("$$") &&
      trimmedLine.endsWith("$$") &&
      trimmedLine.length > 4
    ) {
      // 한 줄 블록 수식
      if (currentTextLines.length > 0) {
        blocks.push({
          type: "text",
          content: currentTextLines.join("\n").trim(),
        });
        currentTextLines = [];
      }

      blocks.push({
        type: "block-math",
        content: trimmedLine.slice(2, -2), // $$ 제거
      });
      i++;
      continue;
    }

    // 표의 시작을 감지 (| 로 시작하고 끝나며, 다음 줄이 구분선)
    if (
      trimmedLine.startsWith("|") &&
      trimmedLine.endsWith("|") &&
      i + 1 < lines.length
    ) {
      const nextLine = lines[i + 1].trim();
      const separatorPattern = /^\|(\s*:?-+:?\s*\|)+$/;

      if (separatorPattern.test(nextLine)) {
        // 현재까지의 텍스트를 블록에 추가
        if (currentTextLines.length > 0) {
          blocks.push({
            type: "text",
            content: currentTextLines.join("\n").trim(),
          });
          currentTextLines = [];
        }

        // 표 블록 수집
        const tableLines = [line, lines[i + 1]]; // 헤더와 구분선
        i += 2;

        // 데이터 행들 수집
        while (i < lines.length) {
          const dataLine = lines[i];
          const trimmedDataLine = dataLine.trim();
          // 빈 줄이거나 표 형태가 아니면 표 끝
          if (
            trimmedDataLine === "" ||
            !trimmedDataLine.startsWith("|") ||
            !trimmedDataLine.endsWith("|")
          ) {
            break;
          }
          tableLines.push(dataLine);
          i++;
        }

        blocks.push({
          type: "table",
          content: tableLines.join("\n"),
        });
        continue;
      }
    }

    // 일반 텍스트 수집
    currentTextLines.push(line);
    i++;
  }

  // 남은 텍스트 추가
  if (currentTextLines.length > 0) {
    blocks.push({
      type: "text",
      content: currentTextLines.join("\n").trim(),
    });
  }

  return blocks.filter(
    (block) => block.content.trim().length > 0 || block.type === "code"
  );
};
